import { policyStatusConflictUserHelp, type QuoteError, quoteFailedUserHelp } from '@korint/folio';
import type { Action, ErrorDictionary, ForUserError } from 'korint-sdk';

const ACTIONS: Record<Action, string> = {
  'PERFORM THIS ACTION': "d'effectuer cette action",
  'CREATE ASSET': 'de créer un véhicule',
  'CREATE ASSET WITH START DATE': 'de créer un véhicule avec une date de début',
  'REPLACE ASSET': 'de remplacer un véhicule',
  'EDIT ASSET WITH START DATE': 'de modifier un véhicule avec une date de début',
  'CHOOSE ASSET OPTIONS': 'de choisir des options pour un véhicule',
  'STOP ASSET': "d'arrêter un véhicule",
  'EDIT ASSET': 'de modifier un véhicule',
  'EDIT CUSTOMER': 'de modifier les informations du client',
  SIGN: 'de signer',
  'EDIT POLICY': 'de modifier une police',
  'CONFIRM POLICY': 'de confirmer une police',
  'QUOTE POLICY': 'de calculer un devis',
  'REMOVE ASSET': 'de retirer un véhicule',
  'SUSPEND POLICY': 'de suspendre une police',
  'CREATE MTA BRANCH': "de créer un projet d'avenant",
  'FORK TO NEW MTA BRANCH': "de créer un projet d'avenant",
  'FORK TO NEW NB BRANCH': 'de créer un nouveau devis',
  'DEFINE PARENT POLICY ID': 'de choisir une police parente',
  'INITIALIZE CUSTOMER': 'de créer un nouveau client',
  'RETRIEVING QUOTE': 'de récupérer un devis',
  'STOP POLICY': "d'arrêter la police",
  'CREATE POLICY': 'de créer une police',
  'ACTIVATE POLICY': "d'activer une police",
  'GENERATE MANDATE SIGNATURE REQUEST': 'de générer une demande de signature de mandat',
  'GENERATE PAYMENT METHOD SECRET': 'de générer un secret de méthode de paiement',
  'CREATE CLAIM': 'de déclarer un sinistre',
  'CREATE BRANCH': 'de créer un projet',
  'FORK TO NEW RENEWAL BRANCH': 'de créer un projet de renouvellement',
  'FINALIZE RENEWAL': 'de finaliser le renouvellement',
  'FORK TO NEW REPRICING BRANCH': 'de créer un projet de re-tarification',
  'SEND CONTRACT TO SIGNATURE': 'de demander la signature du contrat',
  'GENERATE SIGNATURE REQUEST': 'de générer une demande de signature',
  'GENERATE DOCUMENTS FOR SIGNATURE': 'de générer des documents pour la signature',
  'APPROVE DISTRIBUTION REQUEST': 'de valider une demande de distribution',
  'DENY DISTRIBUTION REQUEST': 'de refuser une demande de distribution',
  'WAITING FOR SIGNATURE DISTRIBUTION REQUEST': 'de mettre en attente une demande de signature',
  'DELETE DISTRIBUTION REQUEST': 'de supprimer une demande de distribution',
  REVOKED_DISTRIBUTION_REQUEST: 'de révoquer une demande de distribution',
  'CREATE DEROGATION': 'de créer une dérogation',
  'UPDATE POLICY QUOTE': 'de modifier un devis',
  'EDIT POLICY EXTERNAL ID': "de modifier le numéro de contrat d'une police",
  'DELETE CUSTOMER FROM POLICY': "de supprimer un client d'une police",
};

export const defaultErrorMessage: ForUserError = {
  userError: 'Erreur inconnue',
  userHelp: 'Veuillez réessayer plus tard',
};

export const errorDictionary = {
  // "Normal" errors
  ASSET_NOT_FOUND: e => ({
    userError: 'Véhicule introuvable',
    userHelp: `Le véhicule ${e.details.assetId} n'existe pas`,
  }),
  POLICY_NOT_FOUND: () => ({
    userError: 'Impossible de trouver la police',
    userHelp: "Veuillez vérifier l'identifiant unique de la police",
  }),
  CUSTOMER_NOT_FOUND: () => ({
    userError: 'Impossible de trouver ce client',
    userHelp: "Veuillez vérifier l'identifiant du client",
  }),
  USER_NOT_FOUND: e => ({
    userError: 'Utilisateur introuvable',
    userHelp: `L'utilisateur ${e.details.userId} n'existe pas`,
  }),
  COMPANY_NOT_FOUND: e => ({
    userError: 'Entreprise introuvable',
    userHelp: `L'entreprise ${e.details.siret} n'existe pas`,
  }),
  BRANCH_NOT_FOUND: e => ({
    userError: 'Projet introuvable',
    userHelp: `Le projet ${e.details.branchId} n'existe pas`,
  }),
  INVALID_PRICING_DETAILS: e => {
    if (e.details.errors[0]?.key === 'total retail price') {
      return {
        userError: 'Erreur de tarification',
        userHelp:
          'Le prix total du véhicule et des accessoires doit être entre 200 et 10 000 euros',
      };
    }

    return {
      userError: 'Erreur de tarification',
      userHelp: 'Veuillez vérifier les informations que vous avez saisies',
    };
  },
  CONFLICTING_POLICY_STATUS: e => ({
    userError: `Impossible ${ACTIONS[e.details.action as Action]}`,
    userHelp: policyStatusConflictUserHelp(e.details),
  }),
  CONFLICTING_BRANCH_STATUS: e => {
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';

    return {
      userError: `Impossible ${ACTIONS[e.details.action as Action]}`,
      userHelp: `La police ${requirement} ${e.details.branchStatus}`,
    };
  },
  CONFLICTING_ASSET_STATUS: e => {
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';

    return {
      userError: `Impossible ${ACTIONS[e.details.action as Action]}`,
      userHelp: `Le véhicule ${requirement} ${e.details.assetStatus}`,
    };
  },

  INVALID_BROKERAGE_FIRM: () => ({
    userError: 'Cabinet de courtage invalide',
    userHelp:
      'Le cabinet de courtage créant une police enfant doit être le même que celui de la police parente',
  }),
  BROKERAGE_FIRM_NOT_FOUND: e => ({
    userError: 'Cabinet de courtage introuvable',
    userHelp: `Le cabinet ${e.details.brokerageFirmId} n'existe pas`,
  }),
  INVOICE_NOT_FOUND: e => ({
    userError: 'Facture introuvable',
    userHelp: `La facture ${e.details.invoiceId} n'existe pas`,
  }),
  PAYMENT_METHOD_NOT_FOUND: e => ({
    userError: 'Méthode de paiement introuvable',
    userHelp: `La méthode de paiement ${e.details.paymentMethodId} n'existe pas`,
  }),
  STRIPE_CUSTOMER_NOT_FOUND: e => ({
    userError: 'Client de paiement introuvable',
    userHelp: `Le client de paiement ${e.details.customerId} n'existe pas`,
  }),
  FORBIDDEN: () => ({
    userError: 'Action interdite',
    userHelp: `Vous n'avez pas les droits pour effectuer cette action`,
  }),
  INVALID_INVOICING_CONFIG: () => ({
    userError: 'Configuration de facturation invalide',
    userHelp: `La configuration de facturation n'est pas valide`,
  }),
  INVALID_CONFIRMATION_DATE: e => {
    const frenchConfirmationDate = new Date(e.details.whenAt).toLocaleString('fr-FR');
    const frenchExpirationDate = new Date(e.details.startedAt).toLocaleString('fr-FR');

    return {
      userError: 'Date de confirmation invalide',
      userHelp: `La date de confirmation ${frenchConfirmationDate} doit être antérieure à la date d'expiration du devis ${frenchExpirationDate}`,
    };
  },
  NO_UNIQUE_DOCUMENT_TYPE_READY_TO_BE_SIGNED: () => ({
    userError: 'En attente de document',
    userHelp: 'Le document est en cours de génération',
  }),
  NO_DOCUMENT_READY_TO_BE_SIGNED: () => ({
    userError: 'En attente de document',
    userHelp: 'Les documents sont en cours de génération',
  }),
  INVALID_CUSTOMER_SIRET: e => ({
    userError: 'SIRET invalide',
    userHelp: `Le SIRET ${e.details.siret} n'est pas valide`,
  }),
  INVALID_CUSTOMER_SIREN: e => ({
    userError: 'SIREN invalide',
    userHelp: `Le SIREN ${e.details.siren} n'est pas valide`,
  }),
  REGISTRATION_NUMBER_NOT_FOUND: e => ({
    userError: "Numéro d'immatriculation introuvable",
    userHelp: `La plaque d'immatriculation ${e.details.registrationNumber} n'existe pas`,
  }),

  // 400 that should be avoided with proper integration
  INVALID_PRODUCT: e => ({
    userError: 'Produit invalide',
    userHelp: `Le produit ${e.details.product} n'est pas valide`,
  }),
  INVALID_EMAIL: e => ({
    userError: 'Email invalide',
    userHelp: `L'adresse email ${e.details.email} n'est pas valide`,
  }),
  INVALID_EVENT_CREATION_DATE: e => {
    const { createdAt, latestCreatedAt } = e.details;

    const frenchCreatedAt = new Date(createdAt).toLocaleString('fr-FR');
    const frenchLatestCreatedAt = new Date(latestCreatedAt).toLocaleString('fr-FR');

    return {
      userError: 'Date de création invalide',
      userHelp: `Votre date de simulation (le ${frenchCreatedAt}) doit être postérieure au ${frenchLatestCreatedAt} pour pouvoir effectuer une commande`,
    };
  },
  CONFLICTING_POLICY_TYPE: e => {
    const { policyType } = e.details;
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';
    const action = ACTIONS[e.details.action as Action];

    return {
      userError: 'Type de police invalide',
      userHelp: `Le type de la police ${requirement} ${policyType} afin ${action}`,
    };
  },
  CONFLICTING_CONTRACT_TYPE: e => {
    const { contractType } = e.details;
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';
    const action = ACTIONS[e.details.action as Action];

    return {
      userError: 'Type de police invalide',
      userHelp: `Le type de la police ${requirement} ${contractType} afin ${action}`,
    };
  },
  QUOTE_FAILED: e => {
    const { quoteErrors } = e.details as { quoteErrors?: QuoteError[] };
    return {
      userError: "Impossible d'obtenir une tarification",
      userHelp: quoteFailedUserHelp(quoteErrors),
    };
  },
  CONFLICTING_CONFIG: e => {
    const { configField, value } = e.details;
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';

    const action = ACTIONS[e.details.action as Action];

    if (configField === 'allowDirectSale' && value === 'true') {
      return {
        userError: 'Cabinet de courtage',
        userHelp: 'Vous devez être rattaché à un cabinet de courtage pour effectuer cette action',
      };
    }

    return {
      userError: 'Problème de configuration',
      userHelp: `Le champ de configuration ${configField} ${requirement} ${value} afin ${action}`,
    };
  },
  CONFLICTING_BRANCH_TYPE: e => {
    const requirement = e.details.requirement === 'must be' ? 'doit être' : 'ne doit pas être';
    const action = ACTIONS[e.details.action as Action];

    return {
      userError: 'Type de projet invalide',
      userHelp: `Le type de projet ${requirement} ${e.details.branchType} afin ${action}`,
    };
  },
  INVALID_ASSET_STARTED_AT: e => {
    const frenchEffectDate = new Date(e.details.givenDate).toLocaleString('fr-FR');

    //TODO: update this error in the back to be easier to translate
    return {
      userError: 'Date de lancement invalide',
      userHelp: `La date de lancement ${frenchEffectDate} est invalide`,
    };
  },
  ASSET_NOT_CREATED_ON_THIS_BRANCH: () => ({
    userError: 'Véhicule créé sur un autre projet',
    userHelp: `Le véhicule n'a pas été créé sur ce projet`,
  }),
  // Those should be 500 (or 501) and should not be detailed as user or integrator has no control over it
  BROKERAGE_FIRM_FEES_NOT_FOUND: e => ({
    userError: 'Frais de courtage introuvable',
    userHelp: `Pas de frais de courtage définis pour le produit ${e.details.productId}`,
  }),
  INVOICING_CONFIG_NOT_SET: () => ({
    userError: 'Configuration de facturation manquante',
    userHelp: 'Veuillez configurer la facturation pour cette police',
  }),
  NO_SIGNED_DOCUMENTS_ON_PRODUCT: () => ({
    userError: 'Pas de document à signer',
    userHelp: `Aucun document à signer n'a été configuré`,
  }),
  MISSING_ASSET_OPTIONS: () => ({
    userError: 'Options de véhicule manquantes',
    userHelp: 'Veuillez choisir des options pour le véhicule',
  }),
  QUOTE_NOT_FOUND: () => ({
    userError: 'Devis introuvable',
    userHelp: 'Veuillez vérifier que vous avez bien obtenu un devis',
  }),
  DOCUMENT_CONFIG_NOT_FOUND: () => ({
    userError: 'Configuration de document introuvable',
    userHelp: 'Veuillez vérifier la configuration de document',
  }),
  PRODUCT_NOT_FOUND: () => ({
    userError: 'Produit introuvable',
    userHelp: 'Veuillez vérifier le produit',
  }),
  EXTERNAL_QUOTE_NOT_FOUND: () => ({
    userError: "La tarification externe n'a pas été stockée pour cette police",
    userHelp: 'Nous ne pouvons pas afficher les franchises pour chaque garantie',
  }),
} satisfies ErrorDictionary;
